<template>
  <div class="metrics-container">

    <!-- Encabezado: título principal y filtros -->
    <section class="metrics-header">
      <div class="metrics-header__row">

        <!-- Lado izquierdo: título y rango de fechas -->
        <div class="metrics-header__text">
          <h1 class="metrics-header__title">Métricas</h1>
          <p class="metrics-header__subtitle">
            <ion-icon name="calendar-outline" class="date-icon" />
            <span>{{ formattedDateRange }}</span>
          </p>
        </div>

        <!-- Lado derecho: filtros de fecha y selector de línea -->
        <div class="metrics-filters">

          <!-- Botones de rango rápido -->
          <div class="date-filters">
            <button
              v-for="range in ['Hoy', 'Ayer', 'Semana', 'Mes', 'Personalizado']"
              :key="range"
              class="filter-btn tooltip"
              :class="{ active: selectedDateRange === range }"
              :data-tooltip="range"
              :disabled="loading"
              @click="setDateRange(range)"
            >
              <ion-icon :name="getIconForRange(range)" class="icon-add" />
            </button>
          </div>

          <!-- Selector de línea -->
          <div class="filter-group">
            <ion-icon name="git-branch-outline" class="select-icon"></ion-icon>
            <select
              :value="selectedLineId"
              @change="setLineId($event.target.value)"
              :disabled="loading"
            >
              <option value="">Todas las líneas</option>
              <option
                v-for="line in lines"
                :key="line.id"
                :value="line.id"
              >
                {{ line.line_name }}
              </option>
            </select>
          </div>

        </div>
      </div>
    </section>

    <!-- Modal de selector personalizado de fechas -->
    <DateRangePicker
      v-if="showCustomPicker"
      @cancel="showCustomPicker = false"
      @apply="handleCustomRange"
    />

    <!-- SPINNER DE CARGA -->
    <div v-if="loading" class="loading-spinner">
      <span class="spinner"></span>
      <p>Cargando Métricas...</p>
    </div>

    <section v-else>

      <!-- Gráfico de clientes nuevos por día -->
      <LineChart
        title="Leads Nuevos por día"
        subtitle="Basado en el primer mensaje enviado por cada cliente"
        :labels="newClientsLabels"
        :tooltip-labels="newClientsTooltipLabels"
        :datasets="newClientsDataset"
        :total="newClientsTotal"
      />

      <!-- KPIs: citas confirmadas y conversaciones activas -->
      <div class="metrics-kpi-grid">
        <KPI
          icon="checkmark-done-outline"
          title="Leads Cierre"
          subtitle="Citas confirmadas por respuesta del cliente"
          :value="successfulAppointmentsCurrent"
          :previous="successfulAppointmentsPrevious"
          :change="successfulAppointmentsChange"
          suffix="ctas"
        />

        <KPI
          icon="chatbox-ellipses-outline"
          title="Leads Conversaciones"
          subtitle="Contactos que enviaron 6 o más mensajes"
          :value="activeConvsCurrent"
          :previous="activeConvsPrevious"
          :change="activeConvsChange"
          suffix="ctos"
        />
      </div>

      <!-- Top emojis con los que llegaron clientes con cita exitosa -->
      <EmojiTopList
        :emojis="topEmojiList"
        title="Tendencias de uso de emojis"
        subtitle="Basado en los mensajes iniciales de clientes con cita exitosa"
        :enableDropdown="true"
        :page-size="10"
        @chat="onChat"
      />

      <!-- KPIs: promedio tiempo repuesta y promedio de mensajes -->
      <div class="metrics-kpi-grid">
        <KPI
          icon="time-outline"
          title="Prom. tiempo de respuesta"
          subtitle="Promedio diario en minutos"
          :value="avgResponseCurrent"
          :previous="avgResponsePrevious"
          :change="avgResponseChange"
          suffix="min"
        />

        <KPI
          icon="chatbubble-ellipses-outline"
          title="Prom. mensajes por contacto"
          subtitle="Promedio de mensajes enviados por cada contacto"
          :value="avgMessagesCurrent"
          :previous="avgMessagesPrevious"
          :change="avgMessagesChange"
          suffix="msg"
        />
      </div>

      <!-- Comparativas donuts -->
      <div class="donut-row">
        <DonutChart
          title="Leads Cierre vs Leads Convs."
          subtitle="Proporción de conversaciones que cierran"
          :labels="cierresVsConversacionesLabels"
          :data="cierresVsConversacionesData"
          :total="cierresVsConversacionesTotal"
          :badges="cierresVsConversacionesBadges"
        />

        <DonutChart
          title="Leads Cierre vs Leads Nuevos"
          subtitle="Comparativa entre inicio de contacto y cierre"
          :labels="cierresVsNuevosLabels"
          :data="cierresVsNuevosData"
          :total="cierresVsNuevosTotal"
          :badges="cierresVsNuevosBadges"
        />

        <DonutChart
          title="Leads Nuevos vs Leads Convs."
          subtitle="Proporción según actividad reciente"
          :labels="nuevosVsConversacionesLabels"
          :data="nuevosVsConversacionesData"
          :total="nuevosVsConversacionesTotal"
          :badges="nuevosVsConversacionesBadges"
        />
      </div>

      <BarChart
        title="Tags más usados"
        subtitle="Contactos etiquetados en el rango"
        :labels="tagsLabels"
        :data="tagsData"
        :colors="tagsColors"
        :total="tagsTotal"
      />

      <div class="volume-chart">
        <!-- Gráfico de volumen de mensajes por día -->
        <LineChart
          title="Volumen de mensajes por día"
          subtitle="Total de mensajes enviados por todos los clientes"
          :labels="messageVolumeLabels"
          :tooltip-labels="messageVolumeTooltipLabels"
          :datasets="messageVolumeDataset"
          :total="messageVolumeTotal"
        />
      </div>

    </section>

  </div>
</template>

<script>
import DateRangePicker from '@/components/metrics/DateRangePicker.vue'
import LineChart from '@/components/metrics/LineChart.vue'
import KPI from '@/components/metrics/KPI.vue'
import DonutChart from '@/components/metrics/DonutChart.vue'
import EmojiTopList from '@/components/metrics/EmojiTopList.vue'
import BarChart from '@/components/metrics/BarChart.vue'

export default {
  name: 'MetricsView',

  components: { DateRangePicker, LineChart, KPI, DonutChart, EmojiTopList, BarChart },

  data() {
    return {
      // Filtro de fecha actual (Hoy, Ayer, Semana, etc.)
      selectedDateRange: 'Semana',

      // Línea activa seleccionada
      selectedLineId: '',

      // Rango de fechas actual (inicio / fin)
      startDate: '',
      endDate: '',

      // Control del modal personalizado
      showCustomPicker: false,

      // Lista de líneas disponibles
      lines: [],

      // Todas las métricas
      metrics: {},

      // Spinner
      loading: true,
    }
  },

  async mounted() {
    await this.fetchLines();
    this.setDateRange(this.selectedDateRange);
  },

  computed: {
    // ======================== CLIENTES NUEVOS ========================
    newClientsLabels() {
      return this.metrics.nuevos_clientes?.map(item => item.fecha) || []
    },
    newClientsTooltipLabels() {
      return this.newClientsLabels.map(date => {
        const d = new Date(`${date}T00:00:00`)
        const weekday = d.toLocaleDateString('es-MX', { weekday: 'long' })
        return `${capitalize(weekday)} ${date}`
      })
    },
    newClientsDataset() {
      return [
        {
          label: 'Clientes nuevos',
          data: this.metrics.nuevos_clientes?.map(item => Number(item.total)) || [],
          borderColor: '#2563eb',
          backgroundColor: 'rgba(37, 99, 235, 0.1)',
          tension: 0.4,
          fill: true,
          borderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 5
        }
      ]
    },
    newClientsTotal() {
      return this.metrics.nuevos_clientes?.reduce((sum, item) => sum + item.total, 0) || 0
    },

    // ======================== KPI: Promedio mensajes por contacto ========================
    avgMessagesCurrent() {
      return this.metrics.promedio_mensajes?.promedio_actual || 0
    },
    avgMessagesPrevious() {
      return this.metrics.promedio_mensajes?.promedio_anterior || 0
    },
    avgMessagesChange() {
      return this.metrics.promedio_mensajes?.cambio_porcentual || 0
    },

    // ======================== KPI: Conversaciones activas ========================
    activeConvsCurrent() {
      return this.metrics.conversaciones_activas?.conversaciones_actuales || 0
    },
    activeConvsPrevious() {
      return this.metrics.conversaciones_activas?.conversaciones_anteriores || 0
    },
    activeConvsChange() {
      return this.metrics.conversaciones_activas?.cambio_porcentual || 0
    },

    // ======================== VOLUMEN DE MENSAJES ========================
    messageVolumeLabels() {
      return this.metrics.message_volume?.map(item => item.fecha) || []
    },
    messageVolumeTooltipLabels() {
      return this.messageVolumeLabels.map(date => {
        const d = new Date(`${date}T00:00:00`)
        const weekday = d.toLocaleDateString('es-MX', { weekday: 'long' })
        return `${capitalize(weekday)} ${date}`
      })
    },
    messageVolumeDataset() {
      return [
        {
          label: 'Volumen de mensajes',
          data: this.metrics.message_volume?.map(item => item.total) || [],
          borderColor: '#10b981',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          tension: 0.4,
          fill: true,
          borderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 5
        }
      ]
    },
    messageVolumeTotal() {
      return this.metrics.message_volume?.reduce((sum, item) => sum + item.total, 0) || 0
    },

    // ======================== DONUT: Nuevos vs Conversaciones ========================
    nuevosVsConversacionesLabels() {
      return ['Nuevos', 'Convs.']
    },
    nuevosVsConversacionesData() {
      const { nuevos, conversaciones } = this.metrics.nuevos_vs_conversaciones || { nuevos: 0, conversaciones: 0 }
      return [nuevos, conversaciones]
    },
    nuevosVsConversacionesTotal() {
      return this.metrics.nuevos_vs_conversaciones?.total || 0
    },
    nuevosVsConversacionesBadges() {
      return [
        { label: 'Nuevos', icon: 'ri-user-add-line', color: '#3b82f6' },
        { label: 'Convs.', icon: 'ri-user-heart-line', color: '#f97316' }
      ]
    },

    // ======================== DONUT: Cierres vs Nuevos ========================
    cierresVsNuevosLabels() {
      return ['Cierres', 'Nuevos']
    },
    cierresVsNuevosData() {
      const { cierres, nuevos } = this.metrics.cierres_vs_nuevos || { cierres: 0, nuevos: 0 }
      return [cierres, nuevos]
    },
    cierresVsNuevosTotal() {
      return this.metrics.cierres_vs_nuevos?.nuevos || 0
    },
    cierresVsNuevosBadges() {
      return [
        { label: 'Cierres', icon: 'ri-checkbox-circle-line', color: '#1fad5e' },
        { label: 'Nuevos', icon: 'ri-user-add-line', color: '#3b82f6' }
      ]
    },

    // ======================== DONUT: Cierres vs Conversaciones ========================
    cierresVsConversacionesLabels() {
      return ['Cierres', 'Convs.']
    },
    cierresVsConversacionesData() {
      const { cierres, conversaciones } = this.metrics.cierres_vs_conversaciones || { cierres: 0, conversaciones: 0 }
      return [cierres, conversaciones]
    },
    cierresVsConversacionesTotal() {
      return this.metrics.cierres_vs_conversaciones?.conversaciones || 0
    },
    cierresVsConversacionesBadges() {
      return [
        { label: 'Cierres', icon: 'ri-checkbox-circle-line', color: '#22c55e' },
        { label: 'Convs.', icon: 'ri-user-heart-line', color: '#f97316' }
      ]
    },

    // ======================== KPI: Tiempo de respuesta promedio ========================
    avgResponseCurrent() {
      return this.metrics.avg_response_time?.promedio_actual || 0
    },
    avgResponsePrevious() {
      return this.metrics.avg_response_time?.promedio_anterior || 0
    },
    avgResponseChange() {
      return this.metrics.avg_response_time?.cambio_porcentual || 0
    },

    // ======================== EMOJIS ========================
    topFiveEmojis() {
      return this.topEmojiList.slice(0, 5)
    },
    topEmojiList() {
      return this.metrics.topEmojiList || []
    },

    // ======================== RANGO FORMATEADO ========================
    formattedDateRange() {
      const format = (str) => {
        const [y, m, d] = str.split('-')
        const localDate = new Date(+y, m - 1, +d)
        return localDate.toLocaleDateString('es-MX', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })
      }

      if (!this.startDate || !this.endDate) return ''
      if (this.startDate === this.endDate) return `${format(this.startDate)}`
      return `${format(this.startDate)} al ${format(this.endDate)}`
    },

    // ======================== KPI: Citas exitosas ========================
    successfulAppointmentsCurrent() {
      return this.metrics.exitosas_citas?.exitosas_actuales || 0
    },
    successfulAppointmentsPrevious() {
      return this.metrics.exitosas_citas?.exitosas_anteriores || 0
    },
    successfulAppointmentsChange() {
      return this.metrics.exitosas_citas?.cambio_porcentual || 0
    },

    // ======================== BAR: Tags usados en el cliente ========================
    tagsLabels() {
      return this.metrics.tags_usage?.map(t => t.name) || []
    },
    tagsData() {
      return this.metrics.tags_usage?.map(t => t.total) || []
    },
    tagsColors() {
      return this.metrics.tags_usage?.map(t => t.color || '#9ca3af') || []
    },
    tagsTotal() {
      return this.tagsData.reduce((sum, v) => sum + v, 0)
    }
  },

  methods: {
    // Fija el rango de fechas según el botón seleccionado
    setDateRange(range) {
      this.selectedDateRange = range

      const now = new Date()
      const localNow = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
      const today = localNow.toISOString().slice(0, 10)

      let startDate, endDate

      if (range === 'Hoy') {
        startDate = endDate = today
      } else if (range === 'Ayer') {
        const ayer = new Date(localNow)
        ayer.setDate(ayer.getDate() - 1)
        startDate = endDate = ayer.toISOString().slice(0, 10)
      } else if (range === 'Semana') {
        const semana = new Date(localNow)
        semana.setDate(semana.getDate() - 6)
        startDate = semana.toISOString().slice(0, 10)
        endDate = today
      } else if (range === 'Mes') {
        const mes = new Date(localNow)
        mes.setDate(mes.getDate() - 29)
        startDate = mes.toISOString().slice(0, 10)
        endDate = today
      } else if (range === 'Personalizado') {
        this.showCustomPicker = true
        return
      }

      this.startDate = startDate
      this.endDate = endDate
      this.fetchMetrics()
    },

    // Devuelve el ícono correspondiente a cada rango
    getIconForRange(range) {
      return {
        Hoy: 'calendar-clear-outline',
        Ayer: 'time-outline',
        Semana: 'calendar-outline',
        Mes: 'calendar-number-outline',
        Personalizado: 'create-outline'
      }[range]
    },

    // Cambia la línea activa y muestra en consola
    setLineId(id) {
      this.selectedLineId = id
      this.fetchMetrics()
    },

    // Se ejecuta cuando se aplica un rango personalizado desde el modal
    handleCustomRange({ startDate, endDate }) {
      this.startDate = startDate
      this.endDate = endDate
      this.selectedDateRange = 'Personalizado'
      this.showCustomPicker = false
      this.fetchMetrics()
    },

    // Peticion al Backend para obtener la data de las METRICAS
    async fetchMetrics() {
      this.loading = true
      try {
        const params = {
          startDate: this.startDate,
          endDate: this.endDate,
          lineId: this.selectedLineId === '' ? null : this.selectedLineId
        }

        const response = await this.$axios.get('/metrics', { params })

        this.metrics = response.data
      } catch (error) {
        console.error('Error al obtener métricas:', error)
      } finally {
        this.loading = false
      }
    },

    // Peticion al Backend para obtener la data de las METRICAS
    async fetchLines() {
      try {
        const response = await this.$axios.get("/lines");

        if (Array.isArray(response.data)) {
          this.lines = response.data;
        } else {
          console.error("Respuesta inesperada del servidor:", response.data);
          this.lines = [];
        }

      } catch (error) {
        console.error("Error al obtener las líneas desde el backend:", error);
        this.lines = [];
      }
    },

    // Ir al chat
    onChat(contact) {
      const lineId = contact.line_id || this.selectedLineId
      if (!lineId || !contact?.phone_number) return

      this.$router.push({
        name: 'ChatPage',
        params: { lineId },
        query: { phone: contact.phone_number }
      })
    },
  }
}

function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1)
}
</script>

<style scoped>

/* ========== CONTENEDOR PRINCIPAL ========== */
.metrics-container {
  padding: 20px;
  background: #f9fafb;
  height: 100vh;
  overflow-y: auto; 
}

/* ========== ENCABEZADO ========== */
.metrics-header__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  font-family: 'Manrope', sans-serif;
  gap: 1rem;
}

.metrics-header__text {
  display: flex;
  flex-direction: column;
  text-align: left;
}

.metrics-header__title {
  font-size: 2rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.metrics-header__subtitle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: #334155; 
  margin-top: 0.25rem;
}

.date-icon {
  font-size: 1.15rem;
  color: #3b82f6; 
  margin-bottom: 1px;
}

/* ========== FILTROS ========== */
.metrics-filters {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.date-filters {
  display: flex;
  gap: 10px;
}

/* Estado deshabilitado para los botones */
.filter-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

/* Estado deshabilitado para el select */
.filter-group select:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* === Botón de filtro (normal) === */
.filter-btn {
  background-color: white;
  color: #505050;
  padding: 0.5rem;
  border: 2px solid #e2e8f0;
  border-radius: 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
  outline: none;
}

.filter-btn:hover {
  background-color: #f1f5f9;
  border-color: #cbd5e1;
  transform: scale(1.05);
}

/* === Botón activo === */
.filter-btn.active {
  background-color: #3b82f6;
  color: white;
  border-color: transparent;
  box-shadow: 0 4px 6px rgba(59, 130, 246, 0.15);
}

.filter-btn.active:hover {
  background-color: #2563eb;
  box-shadow: 0 6px 10px rgba(37, 99, 235, 0.25);
}

/* === Focus === */
.filter-btn:focus {
  box-shadow: 0 0 0 3px rgba(147, 197, 253, 0.4);
}

.icon-add {
  font-size: 1.4rem;
  font-weight: 500;
  line-height: 1;
}

/* === Tooltip personalizado === */
.tooltip {
  position: relative;
}

.tooltip::after {
  content: attr(data-tooltip);
  position: absolute;
  top: 125%; 
  left: 50%;
  transform: translateX(-50%) translateY(-8px);
  background-color: #1e293b;
  color: #fff;
  padding: 6px 12px;
  border-radius: 6px;
  white-space: nowrap;
  font-size: 0.75rem;
  font-weight: 500;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.25s ease, transform 0.25s ease;
  z-index: 100;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1); 
}

.tooltip:hover::after {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

/* === Selector de línea === */
.filter-group {
  position: relative;
  display: flex;
  align-items: center;
  width: 180px;
  background-color: #f8f9fa;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
}

.select-icon {
  position: absolute;
  left: 12px;
  font-size: 1.1rem;
  color: #505050;
  pointer-events: none;
}

.filter-group select {
  width: 100%;
  padding: 0.65rem 1rem 0.65rem 2.4rem;
  border: none;
  border-radius: 0.75rem;
  background-color: transparent;
  color: #1e293b;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  appearance: none;
  transition: all 0.2s ease;
}

.filter-group select:focus {
  outline: none;
}

/* === Rango de fechas visible === */
.date-range-display {
  margin-top: 0.5rem;
  font-size: 0.95rem;
  font-weight: 500;
  color: #334155;
}

/* ========== KPI Cards ========== */
.metrics-kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;
}

/* ========== Donut + KPI secundaria ========== */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;
  align-items: start; 
}

.kpi-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column; 
  gap: 1rem;            
}

/* =============== Comparativa Donuts ============= */
.donut-row {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  justify-content: space-between;
  margin: 2rem 0;
}

.donut-row > * {
  flex: 1 1 250px;
}

/* ========== Volumen de Mnesajes ========= */
.volume-chart {
  margin-bottom: 2rem;
}

/* =========== Spinner ============== */
.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  font-size: 16px;
  color: #64748b;
  gap: 16px;
  font-family: 'Manrope', sans-serif;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 5px solid #e2e8f0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s ease-in-out infinite;
  position: relative;
}

.spinner::after {
  content: '';
  position: absolute;
  top: -8px;
  left: -8px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(59, 130, 246, 0.1);
  animation: pulse 1.6s infinite ease-in-out;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.2;
  }
}
</style>